import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { CheckCircle2, Target, Package, Home } from 'lucide-react';
import InfoTooltip from '../../components/ui/InfoTooltip';

function DashboardStats({ stats, onNavigate }) {
    const { t } = useLanguage();

    const percentage = (current, total) => {
        return total > 0 ? Math.round((current / total) * 100) : 0;
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-10">
            {/* 1. TOTAL QUESTS */}
            <div onClick={() => onNavigate('quests')} className="group relative bg-[#09090b] border border-white/5 rounded-sm p-5 cursor-pointer overflow-hidden hover:border-emerald-500/50 transition-all duration-300 shadow-lg hover:shadow-emerald-500/10">
                <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-emerald-500/30 group-hover:border-emerald-500 transition-colors"></div>
                <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-emerald-500/5 rounded-sm flex items-center justify-center group-hover:bg-emerald-500/10 transition-all duration-300 border border-emerald-500/20">
                        <CheckCircle2 className="w-6 h-6 text-emerald-500" strokeWidth={1.5} />
                    </div>
                    <span className="text-4xl font-mono font-bold text-white tracking-tighter">
                        {percentage(stats.quests.completed, stats.quests.total)}%
                    </span>
                </div>
                <div className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1 flex items-center gap-1">
                    {t('total_quests')} <InfoTooltip text="All main quests." />
                </div>
                <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden mt-3">
                    <div className="h-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]" style={{ width: `${percentage(stats.quests.completed, stats.quests.total)}%` }} />
                </div>
            </div>

            {/* 2. KAPPA QUESTS */}
            <div onClick={() => onNavigate('kappa-path')} className="group relative bg-[#09090b] border border-white/5 rounded-sm p-5 cursor-pointer overflow-hidden hover:border-yellow-500/50 transition-all duration-300 shadow-lg hover:shadow-yellow-500/10">
                <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-yellow-500/30 group-hover:border-yellow-500 transition-colors"></div>
                <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-yellow-500/5 rounded-sm flex items-center justify-center group-hover:bg-yellow-500/10 transition-all duration-300 border border-yellow-500/20">
                        <Target className="w-6 h-6 text-yellow-500" strokeWidth={1.5} />
                    </div>
                    <span className="text-4xl font-mono font-bold text-white tracking-tighter">
                        {percentage(stats.kappa.completed, stats.kappa.total)}%
                    </span>
                </div>
                <div className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1 flex items-center gap-1">
                    {t('kappa_quests')} <InfoTooltip text="Required for Kappa." />
                </div>
                <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden mt-3">
                    <div className="h-full bg-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.5)]" style={{ width: `${percentage(stats.kappa.completed, stats.kappa.total)}%` }} />
                </div>
            </div>

            {/* 3. KAPPA ITEMS */}
            <div onClick={() => onNavigate('kappa-items')} className="group relative bg-[#09090b] border border-white/5 rounded-sm p-5 cursor-pointer overflow-hidden hover:border-purple-500/50 transition-all duration-300 shadow-lg hover:shadow-purple-500/10">
                <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-purple-500/30 group-hover:border-purple-500 transition-colors"></div>
                <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-purple-500/5 rounded-sm flex items-center justify-center group-hover:bg-purple-500/10 transition-all duration-300 border border-purple-500/20">
                        <Package className="w-6 h-6 text-purple-500" strokeWidth={1.5} />
                    </div>
                    <span className="text-4xl font-mono font-bold text-white tracking-tighter">
                        {percentage(stats.kappaItems.collected, stats.kappaItems.total)}%
                    </span>
                </div>
                <div className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1 flex items-center gap-1">
                    {t('kappa_items')} <InfoTooltip text="Collector items found." />
                </div>
                <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden mt-3">
                    <div className="h-full bg-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.5)]" style={{ width: `${percentage(stats.kappaItems.collected, stats.kappaItems.total)}%` }} />
                </div>
            </div>

            {/* 4. HIDEOUT */}
            <div onClick={() => onNavigate('hideout')} className="group relative bg-[#09090b] border border-white/5 rounded-sm p-5 cursor-pointer overflow-hidden hover:border-cyan-500/50 transition-all duration-300 shadow-lg hover:shadow-cyan-500/10">
                <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-cyan-500/30 group-hover:border-cyan-500 transition-colors"></div>
                <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-cyan-500/5 rounded-sm flex items-center justify-center group-hover:bg-cyan-500/10 transition-all duration-300 border border-cyan-500/20">
                        <Home className="w-6 h-6 text-cyan-500" strokeWidth={1.5} />
                    </div>
                    <span className="text-4xl font-mono font-bold text-white tracking-tighter">
                        {percentage(stats.hideout.current, stats.hideout.total)}%
                    </span>
                </div>
                <div className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1 flex items-center gap-1">
                    {t('hideout')} <InfoTooltip text="Hideout progress." />
                </div>
                <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden mt-3">
                    <div className="h-full bg-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.5)]" style={{ width: `${percentage(stats.hideout.current, stats.hideout.total)}%` }} />
                </div>
            </div>
        </div>
    );
}

export default DashboardStats;
